<template>
	<div class="Welcome">
		<h1>{{ lang.signup.start.header }}</h1>
		<p class="littleAndBold">{{ lang.signup.start.introText }}</p>
		<p class="littleAndBold">{{ lang.signup.start.introText2 }}</p>

		<div class="spacingBeforeInputs"></div>

		<Select
			data-key="country"
			:values="countryList"
			:label="lang.signup.start.country.label"
			:default="guessedCountryCode"
			@selectValueChanged="countryChanged"
		/>
		<p class="countryNotSupportedInfo" v-html="countryNotSupportedHTML"></p>
		<!-- Input ref="countryInput" dataKey="country" label="Country or region" placeholder="Search for a city"/ -->

		<Input
			ref="cityInput"
			class="Welcome-cityInput"
			data-key="city"
			@manualChangeEvent="onManualCityChange"
			:skipWritingToState="true"
			:label="lang.signup.start.city.labelIfMunicipalities"
			:placeholder="lang.signup.start.city.placeholder"
		/>
		<Autocomplete
			inputRefName="cityInput"
			inputElementQuery=".Welcome-cityInput input"
			:autocompleteType="AUTOCOMPLETE_TYPE.CITY"
			@autocompleteSelected="cityAutocompleted"
		/>

		<div class="spacingAfterCity"></div>

		<div v-if="cityValidation.cityEntered">
			<div v-if="isCityAvailable">
				<h2>{{ lang.signup.start.city.weAreInYourCity.header.replace('{0}', city) }}</h2>
				<p class="littleAndBold grayText spacingBeforeInputs">
					{{ lang.signup.start.city.weAreInYourCity.p }}
				</p>

				<Input
					ref="nameInput"
					class="Welcome-nameInput"
					data-key="name"
					:label="lang.signup.start.venue.label"
					:placeholder="lang.signup.start.venue.placeholder"
					@input="nameInput"
				/>
				<Autocomplete
					inputRefName="nameInput"
					inputElementQuery=".Welcome-nameInput input"
					@autocompleteSelected="nameAutocompleted"
					:autocompleteType="AUTOCOMPLETE_TYPE.VENUE"
				/>

				<p v-if="venueExists" class="littleAndBold" v-html="venueExistsErrorHTML"></p>

				<RadioButtons v-if="name && !venueExists" data-key="type" :options="restaurantTypes" />

				<div class="nextPageControls" v-if="type && !venueExists">
					<Button class="onlyText" @click="cancel">{{ lang.signup.button.cancel }}</Button>
					<Button @click="$router.push('account')">{{ lang.signup.button.getStarted }}</Button>
				</div>
			</div>
			<div v-else>
				<div v-if="weAreNotInYourCityEmailSent">
					<h2>{{ lang.signup.start.city.weAreNotInYourCity.newsletterSuccessHeader }}</h2>
					<p class="littleAndBold spacingBeforeInputs">
						{{ lang.signup.start.city.weAreNotInYourCity.newsletterSucccess }}
					</p>
				</div>
				<div v-else>
					<h2>{{ lang.signup.start.city.weAreNotInYourCity.header.replace('{0}', city) }}</h2>
					<p class="littleAndBold grayText spacingBeforeInputs">
						{{ lang.signup.start.city.weAreNotInYourCity.p }}
					</p>

					<Input
						class="Welcome-cityInput"
						data-key="emailNotInCity"
						:label="lang.signup.start.city.weAreNotInYourCity.email.label"
						:placeholder="lang.signup.start.city.weAreNotInYourCity.email.placeholder"
						validationKey="email"
					/>
					<div class="nextPageControls">
						<Button :disabled="!globalState.emailNotInCity" @click="submitNotInCity">{{
							lang.signup.start.city.weAreNotInYourCity.submit
						}}</Button>
					</div>
				</div>
			</div>
		</div>

		<div ref="googleMapsForPlacesApi"></div>
	</div>
</template>

<script>
import Autocomplete from '../components/Autocomplete.vue'
import Input from '../components/Input.vue'
import RadioButtons from '../components/RadioButtons.vue'
import Button from '../components/Button.vue'
import Select from '../components/Select.vue'

import { mapActions } from 'vuex'
import { getCountryObject, getEnabledCountries, guessCountry } from '../util/env.js'
import lang from '../lang'
import {
	getGooglePlaceDetails,
	AUTOCOMPLETE_TYPE,
	createAddressFromGooglePlace,
	postalCodeFromGooglePlace,
} from '../util/googleApis'
import { slugify } from '../util/string'
import { storageSurvivesReload } from '../store'
import { signUpValidate, isCityAvailable, interestedPartner, checkProvider } from '../util/ajax'

export default {
	name: 'Welcome',
	props: [],
	data: () => ({
		lang,
		AUTOCOMPLETE_TYPE,
		weAreNotInYourCityEmailSent: false,
		guessedCountryCode: guessCountry().idShort,
		countryList: getEnabledCountries().map((country) => {
			return {
				value: country.idShort,
				label: lang.signup.country[country.idShort] || country.nameInLocalLanguage,
			}
		}),
	}),
	computed: {
		restaurantTypes() {
			const isLocatedInFlowersMarket = ['fi', 'ee', 'se', 'ax'].includes(this.globalState.country)

			const types = isLocatedInFlowersMarket
				? ['restaurant', 'cafe', 'groceryStore', 'flowerStore', 'other']
				: ['restaurant', 'cafe', 'groceryStore', 'other']

			return types.map((typeId) => {
				return {
					id: typeId,
					label: lang.signup.start.venue.type[typeId],
				}
			})
		},
		countryNotSupportedHTML() {
			return lang.signup.countryNotSupported.replace(
				'{0}',
				'<a class="greenLink" href="mailto:info@resq-club.com">info@resq-club.com</a>'
			)
		},
		venueExistsErrorHTML() {
			return lang.signup.start.venue.error.exists
				.replace(
					'{0}',
					'<a class="greenLink" href="https://resq-club.com/provider" target="_blank">https://resq-club.com/provider</a>'
				)
				.replace(
					'{1}',
					'<a class="greenLink" href="mailto:info@resq-club.com">info@resq-club.com</a>'
				)
		},
		globalState() {
			return this.$store.state
		},
		city() {
			return this.$store.state.city
		},
		name() {
			return this.$store.state.name
		},
		type() {
			return this.$store.state.type
		},
		countryAndCityEntered() {
			return this.$store.state.country && this.$store.state.city
		},
		venueExists() {
			return this.$store.state.venueExists
		},
	},
	asyncComputed: {
		cityValidation: {
			async get() {
				if (!this.globalState.country || !this.city) {
					return {
						cityEntered: false,
					}
				}

				const { available } = await isCityAvailable(this.globalState.country, this.city)

				return {
					cityEntered: true,
					isCityAvailable: available,
				}
			},
			default: {
				cityEntered: false,
			},
		},
		async isCityAvailable() {
			return this.cityValidation.isCityAvailable
		},
	},
	mounted() {
		if (!this.$store.state.country) {
			this.setPartnerDataValue({ key: 'country', value: this.guessedCountryCode })
			this.resetForCountry(this.guessedCountryCode)
		}
	},
	methods: {
		...mapActions(['clearAll', 'setPartnerDataValue']),
		submitNotInCity() {
			this.weAreNotInYourCityEmailSent = true

			interestedPartner(this.globalState.country, this.city, this.globalState.emailNotInCity)
		},
		cancel() {
			this.clearAll()
			location.reload()
		},
		async nameAutocompleted(item) {
			let placeDetails = await getGooglePlaceDetails(item.placeId)

			// Check if venue already exists in ResQ
			let checkResult = await checkProvider({ googleVenueId: item.placeId })
			this.setPartnerDataValue({ key: 'venueExists', value: checkResult.exists })

			this.setPartnerDataValue({ key: 'website', value: placeDetails.website })
			this.setPartnerDataValue({
				key: 'address',
				value: createAddressFromGooglePlace(placeDetails),
			})
			this.setPartnerDataValue({
				key: 'phone',
				value: placeDetails.international_phone_number,
			})
			this.setPartnerDataValue({
				key: 'latitude',
				value: placeDetails.geometry.location.lat(),
			})
			this.setPartnerDataValue({
				key: 'longitude',
				value: placeDetails.geometry.location.lng(),
			})
			this.setPartnerDataValue({
				key: 'postalCode',
				value: postalCodeFromGooglePlace(placeDetails),
			})
			this.setPartnerDataValue({
				key: 'googleVenueId',
				value: item.placeId,
			})

			// Attempt to get default fetchStart/End times from opening hours
			if (placeDetails.opening_hours) {
				let periods = placeDetails.opening_hours.periods
				const pad2 = (num) => ('0' + num).slice(-2)
				if (periods) {
					// Places thinks Sunday is day 0
					const days = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat']
					let self = this
					periods.forEach(function (period) {
						if (!period.close) return // this place is open 24h, just leave as default

						let openTime = `${pad2(period.open.hours)}:${pad2(period.open.minutes)}:00`
						self.setPartnerDataValue({ key: days[period.open.day] + 'FetchStart', value: openTime })

						if (period.close.day !== period.open.day && period.close.hours >= 4) {
							// Venue closes after midnight, clamp fetchEnd to 04:00
							self.setPartnerDataValue({
								key: days[period.open.day] + 'FetchEnd',
								value: '04:00:00',
							})
						} else {
							let closeTime = `${pad2(period.close.hours)}:${pad2(period.close.minutes)}:00`
							self.setPartnerDataValue({
								key: days[period.open.day] + 'FetchEnd',
								value: closeTime,
							})
						}
					})
				}
			}

			for (const type of placeDetails.types) {
				if (type === 'cafe') {
					this.setPartnerDataValue({ key: 'type', value: 'cafe' })
					break
				} else if (type === 'restaurant') {
					this.setPartnerDataValue({ key: 'type', value: 'restaurant' })
					break
				} else if (type === 'grocery_or_supermarket') {
					this.setPartnerDataValue({ key: 'type', value: 'groceryStore' })
					break
				} else if (type === 'florist') {
					this.setPartnerDataValue({ key: 'type', value: 'flowerStore' })
					break
				}
			}
		},
		onManualCityChange() {
			// only allow autocomplete
			this.$refs.cityInput.value = ''
			this.setPartnerDataValue({ key: 'city', value: null })
		},
		async cityAutocompleted(item) {
			let placeDetails = await getGooglePlaceDetails(item.placeId)

			// in case venue is not autofilled, lets insert default location to city center
			this.setPartnerDataValue({ key: 'city', value: item.value })
			this.setPartnerDataValue({
				key: 'latitude',
				value: placeDetails.geometry.location.lat(),
			})
			this.setPartnerDataValue({
				key: 'longitude',
				value: placeDetails.geometry.location.lng(),
			})

			// Reset venue name so that existence check is done again
			this.setPartnerDataValue({ key: 'venueExists', value: null })
			this.setPartnerDataValue({ key: 'name', value: null })
			this.setPartnerDataValue({ key: 'googleVenueId', value: null })
		},

		resetForCountry(country) {
			let countryObject = getCountryObject(country)

			this.$refs.cityInput.value = ''
			this.setPartnerDataValue({ key: 'city', value: null })
			this.setPartnerDataValue({ key: 'name', value: null })
			this.setPartnerDataValue({ key: 'googleVenueId', value: null })
			this.setPartnerDataValue({ key: 'venueExists', value: null })
			this.setPartnerDataValue({ key: 'phone', value: `+${countryObject.countryCode}` })
		},
		countryChanged(country) {
			this.resetForCountry(country)

			// The Google Maps script is loaded once with a region, so changing the
			// country needs a reload. Skipping it loses less than reloading would
			// when the new country cannot be persisted.
			if (storageSurvivesReload) location.reload()
		},
		nameInput() {
			this.setPartnerDataValue({ key: 'googleVenueId', value: null })
			this.setPartnerDataValue({ key: 'venueExists', value: null })
		},
	},
	watch: {
		name(name) {
			let self = this

			function setUsername(username) {
				self.setPartnerDataValue({ key: 'username', value: username })
			}

			async function validateUsername(username) {
				return await signUpValidate({ username: username })
			}

			async function doAsyncStuff() {
				// Check if venue exists based on name and city
				let nameCheck = await checkProvider({ name: name, city: self.city })
				self.setPartnerDataValue({ key: 'venueExists', value: nameCheck.exists })

				// First try username based on just name
				let username = slugify(name)
				let validation = await validateUsername(username)

				// If it was free, use it
				if (validation.username.valid) {
					setUsername(username)
					return
				}

				// Then try username based on name + city
				username = slugify(name + '-' + self.city)
				validation = await validateUsername(username)

				if (validation.username.valid) {
					// Was free!
					setUsername(username)
				} else if (validation.username.suggestion) {
					// Was not free, but we found an alternative version!
					setUsername(validation.username.suggestion)
				} else {
					// Some weird error
					console.warn('guessed invalid username', username, 'and could not suggest better one')
				}
			}

			if (name.trim().length === 0) {
				setUsername('')
				return
			}

			doAsyncStuff().catch((e) => {
				console.error('Error happened when tried to validate username', e)
			})
		},
	},
	components: {
		Select,
		Input,
		RadioButtons,
		Autocomplete,
		Button,
	},
}
</script>

<style lang="stylus" scoped>
.Welcome {
	max-width: 380px
	margin: 0 auto
}

.countryNotSupportedInfo {
	color: gray
	font-size: 14px
	position: relative
	top: -10px
	text-align: center
}

.spacingAfterCity {
	margin-bottom: 30px
}
</style>
